import { useCallback } from 'react';
import { listVisitors, listVisits } from '../services/visitorService';
import type {
  Visit,
  VisitHistoryFilters,
  Visitor,
  VisitorListFilters,
} from '../types/visitor';
import { visitorErrorMessage } from '../utils/visitorErrors';
import { usePaginatedMasterData } from './usePaginatedMasterData';

function withFriendlyMessage(error: unknown, fallback: string) {
  return Object.assign(new Error(visitorErrorMessage(error, fallback)), error);
}

export function useVisitors() {
  const loadPage = useCallback(
    async (args: { page: number; pageSize: number; search: string }) => {
      try {
        return await listVisitors(args);
      } catch (error) {
        throw withFriendlyMessage(error, 'Unable to load visitors.');
      }
    },
    [],
  );

  return usePaginatedMasterData<Visitor, VisitorListFilters>({
    getId: item => item.visitorId,
    loadPage,
    initialFilters: {},
    errorMessage: 'Unable to load visitors.',
  });
}

export function useVisitorHistory(initialFilters: VisitHistoryFilters) {
  const loadPage = useCallback(
    async (args: {
      page: number;
      pageSize: number;
      search: string;
      filters: VisitHistoryFilters;
    }) => {
      try {
        return await listVisits(args);
      } catch (error) {
        throw withFriendlyMessage(error, 'Unable to load visitor history.');
      }
    },
    [],
  );

  return usePaginatedMasterData<Visit, VisitHistoryFilters>({
    getId: item => item.visitId,
    loadPage,
    initialFilters,
    errorMessage: 'Unable to load visitor history.',
  });
}
